/**
 * MultiViewWindow — individual draggable/resizable panel in multi-view mode.
 * Extracted from SLDViewerModal.tsx (no behavior changes).
 */

import { useState, useRef, useCallback } from 'react';
import type {
  DataPointConfig,
  DataPointRuntimeState,
  AdvancedAlarmRule,
  TriggeredAdvancedRule,
  AlarmState,
} from '@/types/monitoring';
import type { SLDPointMapping, SLDDiagram } from '@/stores/monitoringStore';
import {
  stateClass,
  renderMetricValues,
  EMPTY_POINT_MAPPINGS,
  EMPTY_ADVANCED_RULE_MAPPINGS,
  type WindowLayout,
} from './sldUtils';

export interface MultiViewWindowProps {
  diagram: SLDDiagram;
  layout: WindowLayout;
  datapoints: DataPointConfig[];
  advancedRules: AdvancedAlarmRule[];
  runtimeStates: Map<string, DataPointRuntimeState>;
  triggeredAdvancedRules: TriggeredAdvancedRule[];
  onMaximize: (diagramId: string) => void;
  onPopOut: (diagramId: string) => void;
  onStartDrag: (e: React.MouseEvent, id: string, type: 'drag' | 'resize') => void;
  onBringToFront: (id: string) => void;
  onPointClick: (e: React.MouseEvent, mapping: SLDPointMapping, diagram: SLDDiagram) => void;
}

export default function MultiViewWindow({
  diagram,
  layout,
  datapoints,
  advancedRules,
  runtimeStates,
  triggeredAdvancedRules,
  onMaximize,
  onPopOut,
  onStartDrag,
  onBringToFront,
  onPointClick,
}: MultiViewWindowProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const imgRef = useRef<HTMLImageElement>(null);
  const [imgLoaded, setImgLoaded] = useState(false);

  const dpMappings = diagram.dataPointMappings ?? EMPTY_POINT_MAPPINGS;
  const ruleMappings = diagram.advancedRuleMappings ?? EMPTY_ADVANCED_RULE_MAPPINGS;

  // Calculate scaled point positions based on object-fit: contain
  const getPointStyle = useCallback(
    (imgX: number, imgY: number): React.CSSProperties | null => {
      const content = contentRef.current;
      const img = imgRef.current;
      if (!content || !img || !img.naturalWidth || !img.naturalHeight) return null;

      const cw = content.clientWidth;
      const ch = content.clientHeight;
      const iw = img.naturalWidth;
      const ih = img.naturalHeight;

      const scale = Math.min(cw / iw, ch / ih);
      const offsetX = (cw - iw * scale) / 2;
      const offsetY = (ch - ih * scale) / 2;

      return { left: offsetX + imgX * scale, top: offsetY + imgY * scale };
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [imgLoaded, layout.w, layout.h],
  );

  return (
    <div
      className="sld-multi-window"
      style={{ left: layout.x, top: layout.y, width: layout.w, height: layout.h, zIndex: layout.zIndex }}
      onMouseDown={() => onBringToFront(diagram.id)}
    >
      <div className="sld-multi-window-header" onMouseDown={(e) => onStartDrag(e, diagram.id, 'drag')}>
        <span className="sld-multi-window-title">{diagram.name}</span>
        <span className="sld-multi-window-badge">
          {dpMappings.length + ruleMappings.length} pts
        </span>
        <button
          className="sld-multi-window-btn"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => onPopOut(diagram.id)}
          title="Open in separate window"
        >
          &#x29C9;
        </button>
        <button
          className="sld-multi-window-btn sld-multi-window-maximize"
          onMouseDown={(e) => e.stopPropagation()}
          onClick={() => onMaximize(diagram.id)}
          title="Open in single view"
        >
          &#x2B1A;
        </button>
      </div>
      <div className="sld-multi-window-content" ref={contentRef}>
        <img
          ref={imgRef}
          src={diagram.imageUrl}
          alt={diagram.name}
          onLoad={() => setImgLoaded(true)}
          draggable={false}
        />
        {imgLoaded && (
          <div className="sld-multi-window-points">
            {dpMappings.map((mapping) => {
              const style = getPointStyle(mapping.x, mapping.y);
              if (!style) return null;
              const runtime = runtimeStates.get(mapping.dataPointId);
              const config = datapoints.find((dp) => dp.id === mapping.dataPointId);
              const state: AlarmState = runtime?.state ?? 'no_data';
              return (
                <div
                  key={`dp-${mapping.dataPointId}`}
                  className="sld-multi-point sld-multi-point--clickable"
                  style={style}
                  onClick={(e) => { e.stopPropagation(); onPointClick(e, mapping, diagram); }}
                >
                  <span className={`sld-point-dot ${stateClass(state)}`} />
                  <span className="sld-multi-point-label">{config?.display_name ?? '?'}</span>
                  {renderMetricValues(config, runtime, mapping.displayMetric)}
                </div>
              );
            })}
            {ruleMappings.map((mapping) => {
              const style = getPointStyle(mapping.x, mapping.y);
              if (!style) return null;
              const trig = triggeredAdvancedRules.find((r) => r.rule_id === mapping.advancedRuleId);
              const rule = advancedRules.find((r) => r.id === mapping.advancedRuleId);
              const ruleState = trig ? trig.severity : 'ok';
              return (
                <div key={`rule-${mapping.advancedRuleId}`} className="sld-multi-point" style={style}>
                  <span className={`sld-point-dot sld-point-dot--diamond ${stateClass(ruleState)}`} />
                  <span className="sld-multi-point-label">{rule?.name ?? 'Rule'}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
      <div className="sld-multi-window-resize" onMouseDown={(e) => onStartDrag(e, diagram.id, 'resize')} />
    </div>
  );
}
